import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Wallet, TreePine, Utensils, Gift, AlertTriangle, Save, Scale, Table2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { MoneyInput } from "../components/MoneyInput";
import { PageHeader, Loader } from "../components/shared";
import { useBudgetAllocate } from "../components/BudgetAllocate";
import { BudgetHistory } from "../components/BudgetHistory";

const ROWS = [
  { key: "decoration", labelKey: "plan.decoration", icon: TreePine, to: "/app/decoration", color: "#1B7A4B" },
  { key: "ceia", labelKey: "plan.ceia", icon: Utensils, to: "/app/ceia", color: "#C8102E" },
  { key: "table", labelKey: "budget.table", icon: Table2, to: "/app/table", color: "#8B5CF6" },
  { key: "gifts", labelKey: "nav.gifts", icon: Gift, to: "/app/gifts", color: "#D4AF37", auto: true },
];
const BAND_CLASS = { ok: "#D4AF37", warn: "#F59E0B", over: "#C8102E" };

export default function Budget() {
  const { t } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const [b, setB] = useState(null);
  const [limits, setLimits] = useState({});
  const [totalLimit, setTotalLimit] = useState("");
  const [saving, setSaving] = useState(false);
  const [reb, setReb] = useState({ from: "decoration", to: "gifts", amount: "" });
  const { allocate, node } = useBudgetAllocate();

  const load = (data) => {
    setB(data.budget);
    const lm = {};
    Object.entries(data.budget.categories).forEach(([k, v]) => { lm[k] = v.limit ?? ""; });
    setLimits(lm);
    setTotalLimit(data.budget.total.limit ?? "");
  };
  const [snapshots, setSnapshots] = useState([]);
  const loadSnaps = () => api.get("/budget/snapshots").then(({ data }) => setSnapshots(data.snapshots || [])).catch(() => {});
  useEffect(() => { api.get("/budget").then(({ data }) => load(data)).catch(() => setB({ total: {}, categories: {} })); loadSnaps(); }, []);

  if (!b) return <Loader />;
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const fmt = (v) => formatMoney(symbol, v || 0);

  const save = async () => {
    const newLimits = Object.fromEntries(Object.entries(limits).map(([k, v]) => [k, v === "" ? null : Number(v)]));
    const newTotal = totalLimit === "" ? null : Number(totalLimit);
    // Guard: a category increase that would over-allocate the total must go through the
    // "Rever Meu Orçamento" flow (redistribute / raise total / apply), not a silent PUT.
    const allocated = ROWS.reduce((s, r) => s + (Number(newLimits[r.key]) || 0), 0);
    const increases = ROWS.filter((r) => !r.auto)
      .map((r) => ({ key: r.key, delta: (Number(newLimits[r.key]) || 0) - (b.categories[r.key]?.limit || 0) }))
      .filter((x) => x.delta > 0);
    if (increases.length && newTotal != null && allocated > newTotal + 0.001) {
      const target = increases.sort((a, c) => c.delta - a.delta)[0];
      allocate(target.key, Number(newLimits[target.key]) || 0, () => {
        api.get("/budget").then(({ data }) => load(data)).catch(() => {});
      });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/budget", { total: newTotal, limits: newLimits });
      load(data); toast.success(t("budget.saved")); loadSnaps();
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const rebalance = async () => {
    const amount = Number(reb.amount);
    if (!amount || reb.from === reb.to) { toast.error(t("balert.question")); return; }
    try {
      const { data } = await api.post("/budget/rebalance", { from_category: reb.from, to_category: reb.to, amount });
      load(data); setReb({ ...reb, amount: "" }); toast.success(t("budget.rebalance_done"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const tot = b.total || {};
  const totOver = (tot.over || 0) > 0;

  return (
    <div data-testid="budget-page">
      <PageHeader title={t("budget.title")} subtitle={t("budget.subtitle")} testid="budget-header" />

      {/* Consolidated total */}
      <div className="glass mb-6 rounded-2xl p-6" data-testid="budget-total-card">
        <label className="mb-2 flex items-center gap-2 text-sm text-xmas-slate">
          <Wallet className="h-4 w-4 text-xmas-gold" /> {t("budget.total")}
        </label>
        <div className="flex items-center gap-2">
          <span className="text-2xl text-xmas-gold">{symbol}</span>
          <MoneyInput testid="budget-total-input" value={totalLimit} onChange={setTotalLimit}
            className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 font-mono-num text-2xl text-xmas-snow outline-none focus:border-xmas-gold" />
        </div>
        <div className="mt-5 grid grid-cols-3 gap-4">
          <Stat label={t("budget.planned_amt")} value={fmt(tot.planned)} />
          <Stat label={t("budget.available2")} value={fmt(tot.available)} danger={totOver} testid="budget-available" />
          <Stat label={t("budget.limit")} value={tot.limit != null ? fmt(tot.limit) : "—"} />
        </div>
        {tot.limit ? <Bar pct={Math.min(100, Math.round((tot.planned / tot.limit) * 100))} band={tot.band} /> : null}
        {totOver && (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-xmas-crimson/40 bg-xmas-crimson/10 px-4 py-3 text-sm text-xmas-crimson" data-testid="budget-over-alert">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {t("budget.band_over", { v: fmt(tot.over) })}
          </div>
        )}
      </div>

      {/* Category rows */}
      <div className="space-y-3">
        {ROWS.map((r) => {
          const c = b.categories[r.key] || {};
          const pct = c.limit ? Math.min(100, Math.round((c.planned / c.limit) * 100)) : 0;
          return (
            <div key={r.key} className="glass rounded-2xl p-5" data-testid={`budget-row-${r.key}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: `${r.color}22` }}>
                    <r.icon className="h-5 w-5" style={{ color: r.color }} />
                  </div>
                  <div>
                    <p className="font-display text-base text-xmas-snow">{t(r.labelKey)}</p>
                    <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-[11px] text-xmas-slate" data-testid={`budget-planned-${r.key}`}>
                      <span className="font-mono-num text-xl font-bold leading-none" style={{ color: BAND_CLASS[c.band] || BAND_CLASS.ok }} data-testid={`budget-spent-${r.key}`}>{fmt(c.planned)}</span>
                      {c.limit != null ? <span>{t("budget.of", { p: "", l: fmt(c.limit) }).trim()}</span> : <span>{t("budget.planned_amt")}</span>}
                    </p>
                  </div>
                </div>
                {r.auto ? (
                  <div className="text-right">
                    <p className="font-mono-num text-lg text-xmas-champagne">{fmt(c.planned)}</p>
                    <p className="text-[10px] text-xmas-slate">{t("budget.gifts_auto")}</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-1" title={t("budget.adjust")}>
                    <span className="text-sm text-xmas-slate">{symbol}</span>
                    <MoneyInput testid={`budget-input-${r.key}`} value={limits[r.key] ?? ""}
                      onChange={(v) => setLimits((p) => ({ ...p, [r.key]: v }))}
                      className="w-32 rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-3 py-2 text-right font-mono-num text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
                  </div>
                )}
              </div>
              <Bar pct={pct} band={c.band} />
              {c.band === "warn" && <p className="mt-1.5 text-xs text-xmas-champagne" data-testid={`budget-warn-${r.key}`}>{t("budget.band_warn")}</p>}
              {c.band === "over" && <p className="mt-1.5 flex items-center gap-1 text-xs text-xmas-crimson" data-testid={`budget-over-${r.key}`}><AlertTriangle className="h-3.5 w-3.5" /> {t("budget.band_over", { v: fmt(c.over) })}</p>}
              {r.to && <Link to={r.to} className="mt-2 inline-block text-xs text-xmas-gold hover:underline">{t("cta.see_ideas")}</Link>}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="max-w-md text-xs text-xmas-slate">{t("budget.note")}</p>
        <button onClick={save} disabled={saving} data-testid="budget-save" className="btn-gold inline-flex shrink-0 items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
          <Save className="h-4 w-4" /> {t("budget.save")}
        </button>
      </div>

      {/* Rebalance */}
      <div className="glass mt-8 rounded-2xl p-6" data-testid="budget-rebalance">
        <div className="mb-2 flex items-center gap-2"><Scale className="h-5 w-5 text-xmas-gold" /><h2 className="font-display text-lg text-xmas-snow">{t("budget.rebalance")}</h2></div>
        <p className="mb-4 text-sm text-xmas-slate">{t("budget.rebalance_desc")}</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t("budget.rebalance_from")}>
            <select value={reb.from} data-testid="reb-from" onChange={(e) => setReb({ ...reb, from: e.target.value })} className="rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-3 py-2 text-sm text-xmas-snow outline-none focus:border-xmas-gold">
              {ROWS.map((r) => <option key={r.key} value={r.key}>{t(r.labelKey)}</option>)}
            </select>
          </Field>
          <Field label={t("budget.rebalance_to")}>
            <select value={reb.to} data-testid="reb-to" onChange={(e) => setReb({ ...reb, to: e.target.value })} className="rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-3 py-2 text-sm text-xmas-snow outline-none focus:border-xmas-gold">
              {ROWS.map((r) => <option key={r.key} value={r.key}>{t(r.labelKey)}</option>)}
            </select>
          </Field>
          <Field label={t("budget.rebalance_amount")}>
            <div className="flex items-center gap-1"><span className="text-sm text-xmas-slate">{symbol}</span>
              <MoneyInput testid="reb-amount" value={reb.amount} onChange={(v) => setReb({ ...reb, amount: v })} className="w-28 rounded-lg border border-xmas-gold/20 bg-xmas-navy/50 px-3 py-2 text-right font-mono-num text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
            </div>
          </Field>
          <button onClick={rebalance} data-testid="reb-confirm" className="rounded-full border border-xmas-gold/40 px-5 py-2.5 text-sm font-semibold text-xmas-gold transition hover:bg-xmas-gold/10">{t("budget.rebalance_confirm")}</button>
        </div>
      </div>
      <BudgetHistory snapshots={snapshots} symbol={symbol} onDelete={async (id) => { try { const { data } = await api.delete(`/budget/snapshots/${id}`); setSnapshots(data.snapshots || []); } catch { /* ignore */ } }} />
      {node}
    </div>
  );
}

function Bar({ pct, band }) {
  return (
    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-xmas-navysurface">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: BAND_CLASS[band] || "#D4AF37" }} />
    </div>
  );
}
function Stat({ label, value, danger, testid }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-xmas-slate">{label}</p>
      <p className={`font-mono-num text-xl ${danger ? "text-xmas-crimson" : "text-xmas-snow"}`} data-testid={testid}>{value}</p>
    </div>
  );
}
function Field({ label, children }) {
  return <div><p className="mb-1 text-xs uppercase tracking-wider text-xmas-slate">{label}</p>{children}</div>;
}
